import React, { useEffect } from 'react';
import { Box, HStack } from '@gluestack-ui/themed';

import { Module } from '@/common/api/models/module';
import { Task } from '@/common/api/models/task';
import { StudentPreviewSourceProvider } from '@/common/studentPreview/StudentPreviewSource';
import ModulePreviewHeader from './components/ModulePreviewHeader';
import CoursePreview from './components/CoursePreview';
import ModulePreview from './components/ModulePreview';
import TaskPreview from './components/TaskPreview';
import ModuleDiffScreen from './components/ModuleDiffScreen';
import ModulePreviewNavigation from './components/ModulePreviewNavigation';
import { usePreviewModal, usePreviewNavigation } from './hooks/useModulePreviewStore';

interface ModulePreviewContainerProps {
  module?: Module | null;
  tasks?: Task[];
  sourceModule?: Module | null;
  sourceTasks?: Task[];
  allModules?: Module[];
  onClose?: () => void;
}

/**
 * Main container for the module preview
 * Manages the preview modal and switches between views (course, module, task, diff)
 */
const ModulePreviewContainer: React.FC<ModulePreviewContainerProps> = ({
  module: propModule,
  tasks: propTasks = [],
  sourceModule: propSourceModule = null,
  sourceTasks: propSourceTasks = [],
  allModules: propAllModules = [],
  onClose,
}) => {
  const {
    isOpen, initPreview, closePreview,
  } = usePreviewModal();
  const { activeView, setCanViewDiff } = usePreviewNavigation();

  // Update preview data when props change without resetting navigation state.
  // (openPreview resets activeView/selectedTaskId and is reserved for external
  // "open fresh" calls, e.g. the task editor Preview button.)
  useEffect(() => {
    if (propModule) {
      initPreview({
        module: propModule,
        tasks: propTasks,
        sourceModule: propSourceModule,
        sourceTasks: propSourceTasks,
        allModules: propAllModules,
      });

      // Enable diff view if source module is provided
      setCanViewDiff(!!propSourceModule);
    }
  }, [propModule, propSourceModule, initPreview, propTasks, propSourceTasks, propAllModules, setCanViewDiff]);

  // Call onClose callback when preview is closed
  const handleClose = React.useCallback(() => {
    closePreview();
    if (typeof onClose === 'function') {
      onClose();
    }
  }, [closePreview, onClose]);

  // Close the preview when ESC key is pressed
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('keydown', handleEsc);
      return () => {
        window.removeEventListener('keydown', handleEsc);
      };
    }
    return undefined;
  }, [handleClose]);

  if (!isOpen) return null;

  // Render view content based on active view
  const renderContent = () => {
    switch (activeView) {
      case 'course':
        return <CoursePreview />;
      case 'module':
        return <ModulePreview />;
      case 'task':
        return <TaskPreview />;
      case 'diff':
        return <ModuleDiffScreen />;
      default:
        return <CoursePreview />;
    }
  };

  return (
    <StudentPreviewSourceProvider>
      <Box
        position={onClose ? 'absolute' : 'relative'}
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="$backgroundLight100"
        zIndex={onClose ? 10000 : undefined}
        flex={onClose ? undefined : 1}
      >
        <ModulePreviewHeader onExit={handleClose} />

        <HStack flex={1}>
          <ModulePreviewNavigation />
          <Box flex={1}>
            {renderContent()}
          </Box>
        </HStack>
      </Box>
    </StudentPreviewSourceProvider>
  );
};

export default ModulePreviewContainer;
